import type { Agent, Persisted } from '@tunasong/schemas'
import { getIconByKey } from '@tunasong/icons'
import { features } from '@tunasong/redux'
import type { BlockAffordance } from './block-affordance.js'
import { resolveSectionAtCursor as defaultResolveSectionAtCursor } from './lib/resolve-section-at-cursor.js'
import type { ResolvedSectionBlock } from './lib/resolve-section-at-cursor.js'
import type { AgentDefinition, Command, CommandFn, TunaEditor } from './plugin-types.js'
import { sentenceCase } from './sentence-case.js'

/**
 * Cmd params for an affordance-derived command. Tests invoke `cmd` with
 * this shape; production callers (palette / block-action menus) hand in
 * the same fields when invoking. The structural type sits next to the
 * resolved `Command` because the palette tooling carries a generic
 * `TParams = any` and the typing is enforced at the call sites that
 * build these args, not by `Command.cmd` itself.
 */
interface BlockAffordanceCommandParams {
  editor: TunaEditor
  dispatch: (action: unknown) => unknown
}

/**
 * Module-level injected dependencies, populated at app bootstrap. Tests
 * pass overrides per call; production callers omit them and the default
 * (real) `resolveSectionAtCursor` is used.
 */
export interface BlockAffordanceCommandDeps {
  resolveSectionAtCursor?: (editor: TunaEditor) => ResolvedSectionBlock | null
}

let injectedDeps: BlockAffordanceCommandDeps = {}

/**
 * Called from app bootstrap (apps/tunasong) to inject overrides. Tests
 * bypass this by passing `deps` per call instead.
 */
export const setBlockAffordanceCommandDeps = (deps: BlockAffordanceCommandDeps): void => {
  injectedDeps = deps
}

/**
 * Convert one (AgentDefinition, attached Agent, BlockAffordance) into
 * a palette Command. Name follows `<Agent Name>: <affordance label>`.
 * `cmd` resolves the cursor's enclosing section at invocation time and
 * returns `null` when no match (the palette's disabled-when-not-applicable
 * convention).
 */
export const blockAffordanceToCommand = (
  definition: AgentDefinition,
  agent: Persisted<Agent>,
  affordance: BlockAffordance,
  deps: BlockAffordanceCommandDeps = {}
): Command => {
  // Typed as a plain function returning `null | undefined` so the
  // "not applicable" branches stay explicit. Cast once at the property
  // assignment below because `CommandFn` is `(params) => void | TunaEditor
  // | Promise<...>` — the palette runner already tolerates a null/undefined
  // return at runtime (sibling pattern: tunabrain-next-line sets `cmd: null`
  // when not applicable, but that decision is made at command-build time;
  // here the cursor state is only known when the user invokes the action).
  const run = (params: BlockAffordanceCommandParams): null | undefined => {
    const rootId = params.editor.rootElement?.id
    if (typeof rootId !== 'string' || rootId.length === 0) {
      return null
    }
    const resolver = deps.resolveSectionAtCursor ?? injectedDeps.resolveSectionAtCursor ?? defaultResolveSectionAtCursor
    const block = resolver(params.editor)
    if (!block) {
      return null
    }
    if (affordance.blockTypes !== '*' && !affordance.blockTypes.includes(block.type)) {
      return null
    }
    const blockRef = {
      id: block.id,
      type: block.type,
      entityId: rootId,
      summary: block.summary,
    }
    const { messageTemplate, focus } = affordance.buildAsk(blockRef)
    params.dispatch(features.ui.actions.openAgentComposer({ agentId: agent.id, focus, draft: messageTemplate }))
    return undefined
  }
  return {
    id: `${agent.id}/${affordance.id}`,
    name: `${sentenceCase(definition.name)}: ${affordance.label}`,
    // Prefix-free label for the group submenu; the group trigger already carries
    // the agent name, so the row drops the redundant "<Agent>: " prefix. `name`
    // stays fully-qualified for the flat palette / command search.
    shortName: affordance.label,
    // Group tagging — every command for one agent shares the definition id so the
    // entity context menu can collapse them into a single flyout. The group
    // trigger represents the agent, so its icon is the definition's icon, not the
    // per-affordance icon (rows keep their own `Icon` below).
    groupId: definition.id,
    groupLabel: sentenceCase(definition.name),
    groupIcon: getIconByKey(definition.iconKey),
    Icon: getIconByKey(affordance.iconKey) ?? getIconByKey(definition.iconKey),
    blockOnRun: false,
    cmd: run as unknown as CommandFn,
  }
}
